// src/pages/BusinessInfo.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch } from '../utils/api';
import Swal from 'sweetalert2';
import SubFooter from '../frontend/components/SubFooter';
const API_BASE = (process.env.REACT_APP_API_URL?.trim() || 'https://localhost:7018/api');

function BusinessInfo() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    countryCode: '+234',
    stateOfOperation: '',
    businessCategory: '',
    businessSector: '',
    productsOfInterest: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(true);

  const [apiDropdowns, setApiDropdowns] = useState({
    states: [],
    categories: [],
    sectors: [],
    products: []
  });
  const [isDropdownsLoading, setIsDropdownsLoading] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    fetchApiDropdowns();
    
    const checkAccess = async () => {
      const step1Data = localStorage.getItem('signupStep1');
      const step3Data = localStorage.getItem('signupStep3');
      const userSession = JSON.parse(localStorage.getItem('userSession') || '{}');
      
      if (step1Data && step3Data) {
        console.log('✅ Access granted: Signup flow detected');
        loadSignupData();
        setIsLoadingData(false);
        return;
      }
      
      if (userSession?.sessionId) {
        console.log('✅ Access granted: Login session detected');
        
        if (userSession.isCompleteRegistration === true || userSession.isCompleteRegistration === 1) {
          console.log('🔄 Registration already complete - redirecting to next step');
          toast.info('Registration complete! Continuing...');
          setTimeout(() => {
            navigate('/schedule-activation', { 
              replace: true,
              state: { fromBusinessInfo: true, sessionId: userSession.sessionId }
            });
          }, 1000);
          return;
        }
        
        await loadLoginData(userSession);
        setIsLoadingData(false);
        return;
      }
      
      console.log('🔍 No valid session found, redirecting to /get-started');
      toast.warning('Please start registration or login first');
      setTimeout(() => {
        navigate('/get-started', { replace: true });
      }, 1000);
    };
    
    checkAccess();
  }, [navigate]);

  const fetchApiDropdowns = async () => {
    try {
      const [statesRes, categoriesRes, sectorsRes, productsRes] = await Promise.allSettled([
        authenticatedFetch(`${API_BASE}/Settings/get-all-states`, 'POST', {
          sortColumn: 'StateName',
          sortDirection: 'ASC', 
          start: 0,
          length: 100
        }),
        authenticatedFetch(`${API_BASE}/Settings/business-categories`, 'POST', {}),
        authenticatedFetch(`${API_BASE}/Settings/business-sectors`, 'POST', {}),
        authenticatedFetch(`${API_BASE}/Settings/products-of-interest`, 'POST', {}),
      ]);

      const getData = (res) => {
        if (res.status !== 'fulfilled' || !res.value) return [];
        const data = res.value?.data;
        return Array.isArray(data) ? data : (data?.data || []);
      };

      setApiDropdowns({
        states: getData(statesRes),
        categories: getData(categoriesRes),
        sectors: getData(sectorsRes),
        products: getData(productsRes),
      });

    } catch (error) {
      console.error('Failed to fetch dropdowns:', error);
    } finally {
      setIsDropdownsLoading(false);
    }
  };

  const getItemId = (item) => {
    return item?.stateId?.toString() || item?.id?.toString() || '';
  };

  const getItemLabel = (item) => {
    return item?.stateName || item?.name || item?.label || String(item);
  };

  const getIdFromName = (items, name) => {
    if (!name || !items?.length) return '';
    const found = items.find(item => {
      const itemLabel = getItemLabel(item);
      const itemId = getItemId(item);
      return itemLabel === name || itemId === name?.toString();
    });
    return found ? getItemId(found) : name;
  };

  const loadSignupData = () => {
    try {
      const step1 = JSON.parse(localStorage.getItem('signupStep1') || '{}');
      const step3 = JSON.parse(localStorage.getItem('signupStep3') || '{}');
      
      setFormData(prev => ({
        ...prev,
        businessEmail: step1.email || step3.email || prev.businessEmail,
        businessName: step1.businessName || prev.businessName,
        businessPhone: step1.phone || prev.businessPhone,
        countryCode: step1.countryCode || '+234',
        stateOfOperation: apiDropdowns.states.length > 0 
          ? getIdFromName(apiDropdowns.states, step1.stateOfOperation || step3.stateOfOperation)
          : (step1.stateOfOperation || step3.stateOfOperation || ''),
        businessCategory: apiDropdowns.categories.length > 0
          ? getIdFromName(apiDropdowns.categories, step1.businessCategory || step3.businessCategory)
          : (step1.businessCategory || step3.businessCategory || ''),
        businessSector: apiDropdowns.sectors.length > 0
          ? getIdFromName(apiDropdowns.sectors, step1.businessSector || step3.businessSector)
          : (step1.businessSector || step3.businessSector || ''),
        productsOfInterest: apiDropdowns.products.length > 0
          ? getIdFromName(apiDropdowns.products, step1.productsOfInterest || step3.productsOfInterest)
          : (step1.productsOfInterest || step3.productsOfInterest || '')
      }));
      setSubmitError('');
      setSubmitSuccess('');
    } catch (e) {
      console.warn('Failed to load signup data:', e);
    }
  };

  const loadLoginData = async (session) => {
    try {
      setIsLoadingData(true);
      
      const apiUrl = `${API_BASE}/HMO/get-company-by-session`;
      const payload = { sessionId: session.sessionId.toString() };
      
      const result = await authenticatedFetch(apiUrl, 'POST', payload);
      
      if (result?.success === true && result?.data) {
        const data = result.data;
        
        setFormData(prev => ({
          ...prev,
          businessName: data.businessName || data.BusinessName || prev.businessName,
          businessEmail: data.email || data.Email || session.userEmail || prev.businessEmail,
          businessPhone: data.businessPhone?.replace(/^(\+?\d+)/, '') || data.PhoneNumber?.replace(/^(\+?\d+)/, '') || prev.businessPhone,
          countryCode: data.businessPhone?.match(/^(\+?\d+)/)?.[1] || data.PhoneNumber?.match(/^(\+?\d+)/)?.[1] || '+234',
          stateOfOperation: data.stateOfOperationId?.toString() || data.stateOperationId?.toString() || data.stateOfOperation || prev.stateOfOperation,
          businessCategory: data.businessCategoryId?.toString() || data.categoryId?.toString() || data.businessCategory || data.BusinessCategory || prev.businessCategory,
          businessSector: data.businessSectorId?.toString() || data.sectorId?.toString() || data.businessSector || data.BusinessSector || prev.businessSector,
          productsOfInterest: data.productsOfInterestId?.toString() || data.productId?.toString() || data.productsOfInterest || data.ProductsOfInterest || prev.productsOfInterest
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          businessEmail: session.userEmail || prev.businessEmail
        }));
      }
      setSubmitError('');
      setSubmitSuccess('');
    } catch (error) {
      console.error('💥 Failed to load login data:', error);
      setFormData(prev => ({
        ...prev,
        businessEmail: session.userEmail || prev.businessEmail
      }));
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (submitError) setSubmitError('');
    if (submitSuccess) setSubmitSuccess('');
  };

  const showAlert = (title, message, fieldToFocus = null) => {
    Swal.fire({
      icon: 'warning',
      title: title,
      text: message,
      confirmButtonText: 'OK',
      confirmButtonColor: '#F89423',
      allowOutsideClick: false
    }).then(() => {
      if (fieldToFocus) {
        setTimeout(() => {
          const el = document.querySelector(`[name="${fieldToFocus}"]`);
          if (el) {
            el.focus();
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    });
  };

  // ✅ ENHANCED: Better validation with specific dropdown checks
  const validateForm = () => {
    // Business Name
    if (!formData.businessName.trim()) {
      showAlert('Required Field', 'Please enter your business name', 'businessName');
      return false;
    }
    
    // Business Email
    if (!formData.businessEmail.trim()) {
      showAlert('Required Field', 'Please enter your business email address', 'businessEmail');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.businessEmail)) {
      showAlert('Invalid Email', 'Please enter a valid email address', 'businessEmail');
      return false;
    }
    
    // Business Phone
    if (!formData.businessPhone.trim()) {
      showAlert('Required Field', 'Please enter your business phone number', 'businessPhone');
      return false;
    }
    
    const phoneDigits = formData.businessPhone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      showAlert('Invalid Phone', 'Please enter at least 10 digits for the phone number', 'businessPhone');
      return false;
    }
    
    // ✅ State of Operation - Check if dropdown loaded and if selected
    if (apiDropdowns.states.length === 0) {
      showAlert('Data Error', 'Unable to load states. Please refresh the page and try again.', null);
      return false;
    }
    if (!formData.stateOfOperation) {
      showAlert('Required Field', 'Please select your state of operation', 'stateOfOperation');
      return false;
    }
    
    // ✅ Business Category - Check if dropdown loaded and if selected
    if (apiDropdowns.categories.length === 0) {
      showAlert('Data Error', 'Unable to load business categories. Please refresh the page and try again.', null);
      return false;
    }
    if (!formData.businessCategory) {
      showAlert('Required Field', 'Please select a business category', 'businessCategory');
      return false;
    }
    
    // ✅ Business Sector - Check if dropdown loaded and if selected
    if (apiDropdowns.sectors.length === 0) {
      showAlert('Data Error', 'Unable to load business sectors. Please refresh the page and try again.', null);
      return false;
    }
    if (!formData.businessSector) {
      showAlert('Required Field', 'Please select a business sector', 'businessSector');
      return false;
    }
    
    // ✅ Products of Interest - Optional but validate if dropdown loaded
    if (apiDropdowns.products.length === 0) {
      console.warn('⚠️ Products of interest dropdown not loaded');
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      const step3 = JSON.parse(localStorage.getItem('signupStep3') || '{}');
      const userSession = JSON.parse(localStorage.getItem('userSession') || '{}');
      const sessionId = step3.sessionId || userSession.sessionId;
      
      if (!sessionId) {
        showAlert('Error', 'Session not found. Please login again.');
        return;
      }

      const apiPayload = {
        sessionId: sessionId.toString(),
        businessName: formData.businessName.trim(),
        businessPhone: `${formData.countryCode}${formData.businessPhone.replace(/\D/g, '')}`,
        stateOfOperation: formData.stateOfOperation,
        businessCategory: formData.businessCategory,
        businessSector: formData.businessSector,
        productsOfInterest: formData.productsOfInterest || ''
      };

      const result = await authenticatedFetch(
        `${API_BASE}/HMO/update-business`,
        'POST',
        apiPayload
      );

      if (result.success === false || result.status === 'error') {
        showAlert('Error', result.message || 'Submission failed');
        return;
      }

      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Business information saved successfully!',
        confirmButtonColor: '#F89423',
        timer: 1500,
        showConfirmButton: false
      });
      
      localStorage.setItem('signupStep4', JSON.stringify(formData));
      localStorage.setItem('signupComplete', JSON.stringify({
        ...apiPayload,
        email: formData.businessEmail,
        completedAt: new Date().toISOString()
      }));
      
      if (userSession.sessionId) {
        localStorage.setItem('userSession', JSON.stringify({
          ...userSession,
          isCompleteRegistration: true,
          lastUpdated: new Date().toISOString()
        }));
      }
      
      navigate('/schedule-activation', { 
        state: { 
          welcomeMessage: `Welcome, ${formData.businessName}!`,
          isNewUser: !userSession.sessionId,
          sessionId: sessionId
        } 
      });

    } catch (error) {
      console.error('💥 Business Update Error:', error);
      showAlert('Error', 'Failed to save business information. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    const userSession = JSON.parse(localStorage.getItem('userSession') || '{}');
    if (userSession.sessionId) {
      navigate('/onboarding-dashboard', { state: { fromBusinessInfo: true } });
    } else {
      navigate('/verification-complete');
    }
  };

  if (isLoadingData || isDropdownsLoading) {
    return (
      <div className="business-info-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading form options...</p>
        </div>
      </div>
    );
  }

  const renderApiOptions = (items, placeholder, isState = false) => {
    if (!items || items.length === 0) return <option value="">No options available</option>;
    
    return (
      <>
        <option value="">{placeholder}</option>
        {items.map((item) => {
          const value = isState ? (item.stateId || item.id) : item.id;
          const label = isState ? (item.stateName || item.name) : item.name;
          
          return <option key={value} value={value}>{label}</option>;
        })}
      </>
    );
  };

  const countryCodes = ['+234', '+1', '+44', '+233', '+254', '+27'];

  const isFormValid = formData.businessName && 
                      formData.businessEmail && 
                      formData.businessPhone && 
                      formData.stateOfOperation && 
                      formData.businessCategory && 
                      formData.businessSector;

  return (
    <div className="business-info-page">
      <ToastContainer position="top-right" autoClose={5000} theme="light" />

      <header className="header-area header-one">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center py-3">
            <a href="/" className="brand-logo">
              <img src="images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
            </a>
            <button className="theme-btn style-two" onClick={() => navigate('/')} disabled={isSubmitting || isDropdownsLoading}>← Back to Home</button>
          </div>
        </div>
      </header>

      <section className="business-info-section py-5" style={{ background: '#fafafa', minHeight: '80vh' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              <div className="text-center mb-4" data-aos="fade-down">
                <div className="d-inline-flex align-items-center gap-2">
                  <span className="d-inline-block rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></span>
                </div>
              </div>

              <h2 className="text-center mb-4" data-aos="fade-up" style={{ color: '#5a4fcf' }}>
                Provide your business info
              </h2>

              <div className="alert alert-info border-0 mb-4" role="alert" data-aos="fade-up" style={{ backgroundColor: '#f3f0ff', color: '#5a4fcf', border: '1px solid #d4ceff' }}>
                <div className="d-flex align-items-start">
                  <i className="fas fa-info-circle me-2 mt-1" style={{ color: '#5a4fcf' }}></i>
                  <small>Enter your business details to complete your registration</small>
                </div>
              </div>

              <div className="card shadow-sm border-0" data-aos="fade-up" data-aos-delay="200">
                <div className="card-body p-4 p-md-5">
                  <form onSubmit={handleSubmit}>
                    {submitError && (
                      <div className="alert alert-danger border-0 mb-3" role="alert">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {submitError}
                      </div>
                    )}
                    {submitSuccess && (
                      <div className="alert alert-success border-0 mb-3" role="alert">
                        <i className="fas fa-check-circle me-2"></i>
                        {submitSuccess}
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label fw-semibold small">Business name <span className="text-danger">*</span></label>
                      <input type="text" name="businessName" className="form-control form-control-lg" value={formData.businessName} onChange={handleChange} required placeholder="Enter business name" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }} disabled={isSubmitting || isDropdownsLoading} />
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">Business email <span className="text-danger">*</span></label>
                        <input type="email" name="businessEmail" className="form-control form-control-lg" value={formData.businessEmail} onChange={handleChange} required placeholder="Enter business email" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }} disabled={isSubmitting || isDropdownsLoading} />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">Business phone <span className="text-danger">*</span></label>
                        <div className="input-group">
                          <select name="countryCode" className="form-select" value={formData.countryCode} onChange={handleChange} style={{ maxWidth: '100px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRight: 'none', borderTopRightRadius: '0', borderBottomRightRadius: '0' }} disabled={isSubmitting || isDropdownsLoading}>
                            {countryCodes.map((code) => (<option key={code} value={code}>{code}</option>))}
                          </select>
                          <input type="tel" name="businessPhone" className="form-control form-control-lg" value={formData.businessPhone} onChange={handleChange} required placeholder="Enter business phone" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }} disabled={isSubmitting || isDropdownsLoading} />
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">State of operation <span className="text-danger">*</span></label>
                        <select name="stateOfOperation" className="form-select form-select-lg" value={formData.stateOfOperation} onChange={handleChange} required style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }} disabled={isSubmitting || isDropdownsLoading}>
                          {renderApiOptions(apiDropdowns.states, 'Select state', true)}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">Business category <span className="text-danger">*</span></label>
                        <select name="businessCategory" className="form-select form-select-lg" value={formData.businessCategory} onChange={handleChange} required style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }} disabled={isSubmitting || isDropdownsLoading}>
                          {renderApiOptions(apiDropdowns.categories, 'Select category', false)}
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">Business sector <span className="text-danger">*</span></label>
                        <select name="businessSector" className="form-select form-select-lg" value={formData.businessSector} onChange={handleChange} required style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }} disabled={isSubmitting || isDropdownsLoading}>
                          {renderApiOptions(apiDropdowns.sectors, 'Select sector', false)}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold small">Products of interest</label>
                        <select name="productsOfInterest" className="form-select form-select-lg" value={formData.productsOfInterest} onChange={handleChange} style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }} disabled={isSubmitting || isDropdownsLoading}>
                          {renderApiOptions(apiDropdowns.products, 'Select products (optional)', false)}
                        </select>
                      </div>
                    </div>

                    <div className="d-grid gap-3 mt-4">
                      <button type="submit" className={`theme-btn style-one btn-lg py-3 ${!isFormValid || isSubmitting ? 'disabled' : ''}`} disabled={!isFormValid || isSubmitting || isDropdownsLoading} style={{ fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" style={{ width: '1rem', height: '1rem', borderWidth: '2px' }}></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            Complete Registration <i className="fas fa-arrow-right ms-2"></i>
                          </>
                        )}
                      </button>
                    </div>

                    <div className="text-center mt-3">
                      <button type="button" className="btn btn-link text-muted text-decoration-none" onClick={handleGoBack} disabled={isSubmitting || isDropdownsLoading}>← Go back</button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="text-center mt-4">
                <small className="text-muted">
                  <i className="fas fa-shield-alt me-1"></i>
                  Your business information is secure and encrypted
                </small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SubFooter/>
           
    </div>
  );
}

export default BusinessInfo;